/**
 * ai/contextBuilder.ts
 *
 * Packages all deterministic engine outputs into a structured,
 * AI-consumable context object. This is the contract between
 * the engine layer and the AI layer.
 *
 * Also computes an inputHash so we can detect if state actually changed
 * between AI calls — avoiding unnecessary API hits.
 */

import {
  Candle, SessionMeta, LiquidityZone, GammaResult,
  TrapSignal, RegimeResult, SMCResult, TradeSetup, AppSettings
} from '../../../types';
// SymbolContext import is forward-referenced below to avoid circular dependency
// (services/buildSymbolContext imports from contextBuilder)
import { calcVWAP, calcRelVol, calcSessionLevels } from '../../../utils/calc';
import { analyzeTrend } from '../../../engines/trendEngine';
import { isMarketOpen } from '../../../hooks/useMarketData';
import { getSessionPhaseFromCandle, SessionPhase } from '../../../utils/session';
export type { SessionPhase };  // re-export for legacy consumers

// ─── Types ────────────────────────────────────────────────────────────────────


export interface MarketContext {
  // Identity
  symbol:       string;
  capturedAt:   number;    // Unix ms

  // Session
  sessionPhase: SessionPhase;
  isMarketOpen: boolean;

  // Raw observations (Layer 1)
  raw: {
    price:       number;
    open:        number;
    high:        number;
    low:         number;
    volume:      number;
    vwap:        number;
    vwapDistPct: number;   // % from VWAP
    rvol:        number;
    candleCount: number;
    interval:    string;
    orHigh:      number;
    orLow:       number;
    sessionHigh: number;
    sessionLow:  number;
    prevHigh:    number | null;
    prevLow:     number | null;
    prevClose:   number | null;
    hasOptions:  boolean;
    lastUpdated: number | null;
    priceChgPct: number;   // change from session open
  };

  // Engine detections (Layer 2)
  regime:   RegimeResult | null;
  gamma:    GammaResult | null;
  smc:      SMCResult | null;
  trend:    import('../../../types').TrendResult | null;
  traps:    TrapSignal[];
  zones:    LiquidityZone[];
  setups:   TradeSetup[];

  // Derived summaries (pre-computed for AI)
  topZones:   LiquidityZone[];  // top 5 by significance
  activeTraps: TrapSignal[];    // non-None severity
  topSetups:   TradeSetup[];    // top 3 by confidence

  // Hash of this context for change detection
  inputHash: string;

  // v5.5: deterministic actionability gate
  setupActionable: boolean;

  // v5.5 Patch 10/10: canonical truth block — AI must follow, not override
  setupTruth?: import('../../../types').SetupTruth;
}

// ─── Session phase detection ──────────────────────────────────────────────────

// ─── Simple hash (change detection) ──────────────────────────────────────────
// Not cryptographic — just needs to detect state changes reliably.

function simpleHash(obj: any): string {
  const str = JSON.stringify(obj, (_, v) =>
    typeof v === 'number' ? Math.round(v * 100) / 100 : v
  );
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}

// ─── Main context builder ─────────────────────────────────────────────────────

export function buildMarketContext(
  symbol:     string,
  candles:    Candle[],
  meta:       SessionMeta,
  regime:     RegimeResult | null,
  gamma:      GammaResult | null,
  smc:        SMCResult | null,
  traps:      TrapSignal[],
  zones:      LiquidityZone[],
  setups:     TradeSetup[],
  settings:   AppSettings,
  interval:   string,
  hasOptions?: boolean   // Defect 4: true source from SymbolContext, overrides gamma proxy
): MarketContext {
  if (!candles.length) {
    return {
      symbol, capturedAt: Date.now(),
      sessionPhase: 'pre-market', isMarketOpen: false,
      raw: { price: 0, open: 0, high: 0, low: 0, volume: 0,
             vwap: 0, vwapDistPct: 0, rvol: 0, candleCount: 0,
             interval, orHigh: 0, orLow: 0, sessionHigh: 0, sessionLow: 0,
             prevHigh: null, prevLow: null, prevClose: null,
             hasOptions: false, lastUpdated: null, priceChgPct: 0 },
      regime: null, gamma: null, smc: null, trend: null,
      traps: [], zones: [], setups: [],
      topZones: [], activeTraps: [], topSetups: [],
      inputHash: 'empty',
      setupActionable: false,
    };
  }

  const cur     = candles[candles.length - 1];
  const vwapArr = calcVWAP(candles);
  const vwap    = vwapArr[vwapArr.length - 1];
  const rvol    = calcRelVol(candles, settings.rvolWindow);
  const levels  = calcSessionLevels(candles);
  const priceChgPct = candles.length > 1
    ? ((cur.close - candles[0].open) / candles[0].open * 100)
    : 0;

  const raw = {
    price:       cur.close,
    open:        cur.open,
    high:        cur.high,
    low:         cur.low,
    volume:      cur.volume,
    vwap,
    vwapDistPct: ((cur.close - vwap) / vwap * 100),
    rvol,
    candleCount: candles.length,
    interval,
    orHigh:      levels.orHigh,
    orLow:       levels.orLow,
    sessionHigh: levels.sessionHigh,
    sessionLow:  levels.sessionLow,
    prevHigh:    meta.prevHigh ?? null,
    prevLow:     meta.prevLow  ?? null,
    prevClose:   meta.prevClose ?? null,
    hasOptions:  hasOptions !== undefined ? Boolean(hasOptions) : Boolean(gamma),  // Defect 4: prefer true source over gamma proxy
    lastUpdated: cur.ts,
    priceChgPct,
  };

  const topZones   = zones.filter(z => z.sig === 'High').sort((a,b) => a.distPct - b.distPct).slice(0,5);
  const activeTraps = traps.filter(t => t.severity !== 'None');
  const topSetups  = [...setups].sort((a,b) => b.confidence - a.confidence).slice(0,3);

  // Patch 2: include full setup maturity/alignment state in hash so AI re-runs on state change
  const topSetupHash = topSetups.map(s => ({
    type:                    s.type,
    dir:                     s.dir,
    conf:                    Math.round(s.confidence),
    maturity:                s.maturity ?? null,
    alignment:               s.alignment ?? null,
    stateTransitionConfirmed: s.stateTransitionConfirmed ?? false,
    confirmationMissing:     s.confirmationMissing ?? [],
  }));

  // Derive setupActionable from the best qualifying setup (not just index 0)
  const bestActionableSetup = setups.find(s =>
    s.maturity === 'actionable' ||
    (s.maturity === 'confirmed' && s.alignment === 'aligned') ||
    s.alignment === 'countertrend-confirmed'
  );
  const setupActionable = Boolean(bestActionableSetup);

  // Hash the key state for change detection
  const hashInput = {
    price:     Math.round(raw.price * 100),
    vwapDist:  Math.round(raw.vwapDistPct * 10),
    regime:    regime?.dominant,
    regimeConf: regime ? Math.round(regime.confidence / 10) : 0,
    smcBias:   smc?.bias,
    sweepRev:  smc?.sweepAndReverse,
    choch:     smc?.lastCHOCH?.candleIdx,
    bos:       smc?.lastBOS?.candleIdx,
    traps:     activeTraps.map(t => t.type).join(','),
    regimeScore: regime ? Object.values(regime.scores).map(v => Math.round(v/5)*5).join(',') : '',
    topSetups: topSetupHash,   // Patch 2: setup state drives AI refresh
    // Patch 10/10: canonical truth in hash — AI re-runs on actionability change
    setupActionable,
    setupTruthMat:   setupActionable ? (bestActionableSetup?.maturity ?? null) : (setups[0]?.maturity ?? null),
    setupTruthAlign: setupActionable ? (bestActionableSetup?.alignment ?? null) : (setups[0]?.alignment ?? null),
  };

  return {
    symbol,
    capturedAt:   Date.now(),
    sessionPhase: getSessionPhaseFromCandle(candles[candles.length - 1]?.ts ?? Date.now()),
    isMarketOpen: isMarketOpen(),
    raw,
    regime, gamma, smc, trend: analyzeTrend(candles), traps, zones, setups,
    topZones, activeTraps, topSetups,
    inputHash: simpleHash(hashInput),
    setupActionable,
    setupTruth: {
      displaySetupType:        topSetups[0]?.type ?? null,
      actionableSetupType:     bestActionableSetup?.type ?? null,
      actionableSetupDir:      (bestActionableSetup?.dir === 'long' || bestActionableSetup?.dir === 'short')
                                 ? bestActionableSetup.dir : null,
      maturity:                (bestActionableSetup ?? topSetups[0])?.maturity ?? null,
      alignment:               (bestActionableSetup ?? topSetups[0])?.alignment ?? null,
      countertrend:            (bestActionableSetup ?? topSetups[0])?.countertrend ?? false,
      stateTransitionConfirmed: (bestActionableSetup ?? topSetups[0])?.stateTransitionConfirmed ?? false,
      confirmationMissing:     (bestActionableSetup ?? topSetups[0])?.confirmationMissing ?? [],
    },
  };
}
// ─── Pipeline note ─────────────────────────────────────────────────────────────
// CANONICAL ARCHITECTURE:
//   SymbolContext   = canonical deterministic symbol state (services/buildSymbolContext.ts)
//   MarketContext   = AI adapter derived from SymbolContext (this file)
//
// Preferred call path:
//   buildSymbolContext()         → SymbolContext
//   buildSymbolContextForAI()   → MarketContext  (adapter in services/buildSymbolContext.ts)
//   analyzeSymbol(MarketContext) → AI thesis      (ai/aiOrchestrator.ts)
//
// No component may manually assemble a MarketContext from raw engine outputs.
// All AI context must originate from buildSymbolContextForAI().
